import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  MessageSquare,
  Send,
  Search,
  User,
  Circle,
  RefreshCw
} from 'lucide-react';
import messagingService from '../../services/MessagingService';

const MessageCenter = ({ clinicians, patientsData, onUnreadCountChange }) => {
  const [allPatients, setAllPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [conversations, setConversations] = useState({});
  const [currentConversation, setCurrentConversation] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Flatten all patients from all clinicians
    const patients = [];
    Object.keys(patientsData).forEach(clinicianId => {
      const clinician = clinicians.find(c => c.id === clinicianId);
      const clinicianName = clinician?.name || clinician?.email?.split('@')[0] || 'Unknown';

      patientsData[clinicianId].forEach(patient => {
        patients.push({
          ...patient,
          clinicianId,
          clinicianName
        });
      });
    });

    setAllPatients(patients);
    loadAllConversations();
  }, [clinicians, patientsData]);

  useEffect(() => {
    // Subscribe to real-time messages
    const unsubscribe = messagingService.onNewMessage((newMessage) => {
      // Update conversations when new message arrives
      loadAllConversations();

      // If the message is for the currently selected patient, update the conversation
      if (selectedPatient && newMessage.sender_id === selectedPatient.id) {
        setCurrentConversation(prev => [...prev, {
          text: newMessage.message,
          sender: 'patient',
          timestamp: new Date(newMessage.created_at),
          ...newMessage
        }]);
      }
    });

    return () => unsubscribe();
  }, [selectedPatient]);

  useEffect(() => {
    scrollToBottom();
  }, [currentConversation]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadAllConversations = async () => {
    try {
      setLoading(true);
      const allConversations = await messagingService.getAllConversations();
      setConversations(allConversations);

      // Count unread messages
      let unreadCount = 0;
      Object.values(allConversations).forEach(conv => {
        conv.forEach(msg => {
          if (!msg.read && msg.sender_type === 'patient') {
            unreadCount++;
          }
        });
      });

      if (onUnreadCountChange) {
        onUnreadCountChange(unreadCount);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading conversations:', error);
      setLoading(false);
    }
  };

  const selectPatient = async (patient) => {
    setSelectedPatient(patient);

    try {
      // Load conversation with this patient
      const conversation = await messagingService.getPatientConversation(patient.id);

      // Format messages for display
      const formattedMessages = conversation.map(msg => ({
        text: msg.message,
        sender: msg.sender_type === 'mendy' ? 'mendy' : 'patient',
        timestamp: new Date(msg.created_at),
        ...msg
      }));

      setCurrentConversation(formattedMessages);

      // Mark messages as read
      const unreadMessageIds = conversation
        .filter(msg => !msg.read && msg.sender_type === 'patient')
        .map(msg => msg.id);

      if (unreadMessageIds.length > 0) {
        await messagingService.markAsRead(unreadMessageIds);
        loadAllConversations(); // Refresh to update unread counts
      }
    } catch (error) {
      console.error('Error loading patient conversation:', error);
      setCurrentConversation([]);
    }
  };

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedPatient || sending) return;

    const messageText = messageInput;
    setMessageInput('');
    setSending(true);

    try {
      // Add message to UI immediately for better UX
      const tempMessage = {
        text: messageText,
        sender: 'mendy',
        timestamp: new Date()
      };
      setCurrentConversation(prev => [...prev, tempMessage]);

      // Send to database
      await messagingService.sendMendyMessage(selectedPatient.id, messageText);

      // Refresh conversations
      loadAllConversations();
    } catch (error) {
      console.error('Error sending message:', error);
      // Remove the temporary message on error
      setCurrentConversation(prev => prev.slice(0, -1));
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getUnreadCount = (patientId) => {
    const conv = conversations[patientId] || [];
    return conv.filter(msg => !msg.read && msg.sender_type === 'patient').length;
  };

  const getLastMessage = (patientId) => {
    const conv = conversations[patientId] || [];
    if (conv.length === 0) return null;
    return conv[conv.length - 1];
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const filteredPatients = allPatients.filter(patient => {
    const searchLower = searchQuery.toLowerCase();
    const name = `${patient.firstname || patient.firstName || ''} ${patient.lastinitial || patient.lastInitial || ''}`.toLowerCase();
    return name.includes(searchLower);
  });

  return (
    <Card className="h-[calc(100vh-200px)]">
      <CardContent className="p-0 h-full flex">
        {/* Left Sidebar - Patient List */}
        <div className="w-80 border-r flex flex-col">
          <div className="p-4 border-b">
            <h3 className="font-semibold text-lg mb-3">Patient Messages</h3>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search patients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : filteredPatients.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <MessageSquare className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">No patients found</p>
              </div>
            ) : (
              filteredPatients.map((patient) => {
                const unreadCount = getUnreadCount(patient.id);
                const lastMessage = getLastMessage(patient.id);
                const isSelected = selectedPatient?.id === patient.id;

                return (
                  <div
                    key={patient.id}
                    onClick={() => selectPatient(patient)}
                    className={`p-4 border-b cursor-pointer transition-colors ${
                      isSelected ? 'bg-blue-50 border-l-4 border-l-blue-600' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="font-medium text-sm">
                          {patient.firstname || patient.firstName} {patient.lastinitial || patient.lastInitial}
                        </span>
                      </div>
                      {unreadCount > 0 && (
                        <span className="bg-blue-600 text-white text-xs rounded-full px-2 py-0.5 font-semibold">
                          {unreadCount}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mb-1">
                      Under {patient.clinicianName}
                    </p>
                    {lastMessage && (
                      <p className="text-xs text-gray-600 truncate">
                        {lastMessage.sender_type === 'mendy' ? 'You: ' : ''}
                        {lastMessage.message}
                      </p>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right Panel - Conversation */}
        <div className="flex-1 flex flex-col">
          {selectedPatient ? (
            <>
              {/* Conversation Header */}
              <div className="p-4 border-b bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-lg">
                      {selectedPatient.firstname || selectedPatient.firstName}{' '}
                      {selectedPatient.lastinitial || selectedPatient.lastInitial}
                    </h3>
                    <p className="text-sm text-gray-600">
                      Under {selectedPatient.clinicianName} • Room{' '}
                      {selectedPatient.roomnumber || selectedPatient.roomNumber || 'N/A'}
                    </p>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      selectedPatient.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {selectedPatient.status}
                  </span>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {currentConversation.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    <div className="text-center">
                      <MessageSquare className="h-16 w-16 mx-auto mb-3 text-gray-300" />
                      <p>No messages yet</p>
                      <p className="text-sm mt-1">Start the conversation by sending a message</p>
                    </div>
                  </div>
                ) : (
                  currentConversation.map((msg, index) => (
                    <div
                      key={index}
                      className={`flex ${msg.sender === 'mendy' ? 'justify-start' : 'justify-end'}`}
                    >
                      <div
                        className={`max-w-[70%] rounded-lg p-3 ${
                          msg.sender === 'mendy'
                            ? 'bg-gray-100 text-gray-900'
                            : 'bg-blue-600 text-white'
                        }`}
                      >
                        <p className="text-sm">{msg.text}</p>
                        <p
                          className={`text-xs mt-1 ${
                            msg.sender === 'mendy' ? 'text-gray-500' : 'text-blue-100'
                          }`}
                        >
                          {formatTime(msg.timestamp)}
                        </p>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="p-4 border-t bg-gray-50">
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your message as Mendy..."
                    className="flex-1"
                    disabled={sending}
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={!messageInput.trim() || sending}
                    className="bg-[#6d4f79] hover:bg-[#5a3f66] text-white"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Messages sent here will appear in the patient's "Chat with Mendy" as coming from Mendy
                </p>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <MessageSquare className="h-20 w-20 mx-auto mb-4 text-gray-300" />
                <h3 className="text-xl font-semibold mb-2">Select a Patient</h3>
                <p>Choose a patient from the list to view and respond to their messages</p>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default MessageCenter;
